// src/pages/seeker/SeekerJobs.jsx

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { jobAdPublicAPI } from '../../services/api';
import { LayoutList, LayoutGrid } from 'lucide-react';

const formatAddress = (addr) => {
  if (!addr) return '';
  if (typeof addr === 'string') return addr;
  return [addr.suburb, addr.state, addr.country].filter(Boolean).join(', ');
};

const ViewToggle = ({ view, setView }) => (
  <div className="view-toggle">
    <button className={`view-toggle-btn ${view === 'list' ? 'active' : ''}`} onClick={() => setView('list')}>
      <LayoutList size={13} /> List
    </button>
    <button className={`view-toggle-btn ${view === 'card' ? 'active' : ''}`} onClick={() => setView('card')}>
      <LayoutGrid size={13} /> Cards
    </button>
  </div>
);

export const SeekerJobs = () => {
  const [jobs, setJobs]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView]       = useState('list');
  const [search, setSearch]   = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    jobAdPublicAPI.getActive()
      .then(({ data }) => setJobs(data.data.ads || []))
      .finally(() => setLoading(false));
  }, []);

  const filtered = jobs.filter(j =>
    j.title?.toLowerCase().includes(search.toLowerCase()) ||
    j.groupProfile?.profileName?.toLowerCase().includes(search.toLowerCase())
  );

  const goToJob = (job) => navigate(`/seeker/jobs/${job.id}`, { state: { ad: job } });

  const rowStyle = {
    cursor: 'pointer',
    transition: 'background 0.1s',
  };

  return (
    <div className="fade-in">
      <div className="page-header">
        <h1>Browse Jobs</h1>
        <p>{jobs.length} jobs available</p>
      </div>

      {/* Search + toggle */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
        <input
          className="form-input"
          placeholder="Search jobs or companies..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ flex: 1 }}
        />
        <ViewToggle view={view} setView={setView} />
      </div>

      {loading ? (
        <div style={{ background: 'white', border: '1px solid var(--border)', borderRadius: 4, padding: '40px', textAlign: 'center', color: 'var(--text-muted)' }}>
          Loading...
        </div>
      ) : filtered.length === 0 ? (
        <div style={{ background: 'white', border: '1px solid var(--border)', borderRadius: 4, padding: '40px', textAlign: 'center', color: 'var(--text-muted)' }}>
          No jobs found
        </div>
      ) : view === 'list' ? (
        <div className="job-list">
          <div className="job-list-header">
            Available Jobs
            <span style={{ color: 'rgba(255,255,255,0.6)', fontSize: '11px' }}>{filtered.length} listings</span>
          </div>
          {filtered.map(job => {
            const pos = job.positions?.[0];
            return (
              <div key={job.id} className="job-row" style={rowStyle} onClick={() => goToJob(job)}
                onMouseEnter={e => e.currentTarget.style.background = '#f8f9fa'}
                onMouseLeave={e => e.currentTarget.style.background = ''}
              >
                <div className="job-row-logo">{job.groupProfile?.profileName?.[0] || 'C'}</div>
                <div className="job-row-main">
                  <div className="job-row-title">{job.title}</div>
                  <div className="job-row-company">{job.groupProfile?.profileName}</div>
                  <div className="job-row-meta">
                    {pos?.workplace?.address && <span className="tag tag-ghost">{formatAddress(pos.workplace.address)}</span>}
                    {pos?.workArrangement    && <span className="tag tag-ghost">{pos.workArrangement}</span>}
                    {pos?.schedule           && <span className="tag tag-ghost">{pos.schedule.replace('_', ' ')}</span>}
                    {job.positions?.length > 1 && <span className="tag tag-ghost">{job.positions.length} positions</span>}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="job-card-grid">
          {filtered.map(job => {
            const pos = job.positions?.[0];
            return (
              <div key={job.id} className="job-card" style={rowStyle} onClick={() => goToJob(job)}
                onMouseEnter={e => e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)'}
                onMouseLeave={e => e.currentTarget.style.boxShadow = ''}
              >
                <div className="job-card-logo">{job.groupProfile?.profileName?.[0] || 'C'}</div>
                <div className="job-card-title">{job.title}</div>
                <div className="job-card-company">{job.groupProfile?.profileName}</div>
                <div className="job-card-meta">
                  {pos?.workplace?.address && <span className="tag tag-ghost">{formatAddress(pos.workplace.address)}</span>}
                  {pos?.workArrangement    && <span className="tag tag-ghost">{pos.workArrangement}</span>}
                  {pos?.schedule           && <span className="tag tag-ghost">{pos.schedule.replace('_', ' ')}</span>}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
